// =============================================================================
// Data-access facade.
//
// Screens import from here and never care which implementation answers:
//
//   api.supabase.js  the real thing
//   api.mock.js      in-memory fixtures, VITE_MOCK=1
//
// Mock mode exists so the UI can be built and demonstrated before a Supabase
// project exists, and so a workflow can be walked through without touching real
// data. It is NOT a test double for the rules engine — the rules live in the
// database and are tested there (supabase/tests). The mock reproduces just
// enough behaviour to drive the screens.
// =============================================================================

import { ENV } from './env.js';
import { tracked } from './activity.js';

export const MOCK = ENV.VITE_MOCK === '1';

const real = MOCK
  ? await import('./api.mock.js')
  : await import('./api.supabase.js');

if (MOCK) console.warn('[library] MOCK MODE — no real data is being read or written.');

// Every call is counted, so the shell can tell "slow" from "stuck" and offer a
// retry (lib/activity.js). This is the one chokepoint every screen already goes
// through — doing it per screen would be fifteen places to forget.
const impl = new Proxy(real, {
  get: (target, prop) => {
    const value = target[prop];
    return typeof value === 'function' ? tracked(value) : value;
  },
});

export const searchMembers     = (...a) => impl.searchMembers(...a);
export const checkEligibility  = (...a) => impl.checkEligibility(...a);
export const activeLoansFor    = (...a) => impl.activeLoansFor(...a);
export const findCopyByCode    = (...a) => impl.findCopyByCode(...a);
export const activeLoanForCopy = (...a) => impl.activeLoanForCopy(...a);
export const issueBooks        = (...a) => impl.issueBooks(...a);
export const returnCopy        = (...a) => impl.returnCopy(...a);
export const renewLoan         = (...a) => impl.renewLoan(...a);
export const findTitlesByName   = (...a) => impl.findTitlesByName(...a);
export const listCategories     = (...a) => impl.listCategories(...a);
export const addTitleWithCopies = (...a) => impl.addTitleWithCopies(...a);
export const addCopiesToTitle   = (...a) => impl.addCopiesToTitle(...a);
export const listMembers        = (...a) => impl.listMembers(...a);
export const getMember          = (...a) => impl.getMember(...a);
export const getMemberSummary   = (...a) => impl.getMemberSummary(...a);
export const memberPayments     = (...a) => impl.memberPayments(...a);
export const memberLoanHistory  = (...a) => impl.memberLoanHistory(...a);
export const listBranches       = (...a) => impl.listBranches(...a);
export const createMember       = (...a) => impl.createMember(...a);
export const updateMember       = (...a) => impl.updateMember(...a);
export const setMemberStatus    = (...a) => impl.setMemberStatus(...a);
export const recordPayment      = (...a) => impl.recordPayment(...a);
export const branchSettings     = (...a) => impl.branchSettings(...a);
export const setMembershipFee   = (...a) => impl.setMembershipFee(...a);
export const listStaff          = (...a) => impl.listStaff(...a);
export const createStaff        = (...a) => impl.createStaff(...a);
export const setStaffActive     = (...a) => impl.setStaffActive(...a);
export const updateBranchSettings = (...a) => impl.updateBranchSettings(...a);
export const allBranchSettings  = (...a) => impl.allBranchSettings(...a);
export const listOverdue        = (...a) => impl.listOverdue(...a);
export const countOverdue       = (...a) => impl.countOverdue(...a);
export const listCatalogue      = (...a) => impl.listCatalogue(...a);
export const titleCopies        = (...a) => impl.titleCopies(...a);
export const copiesForTitles    = (...a) => impl.copiesForTitles(...a);
export const updateTitle        = (...a) => impl.updateTitle(...a);
export const titleLoanHistory   = (...a) => impl.titleLoanHistory(...a);
export const updateCopyCondition = (...a) => impl.updateCopyCondition(...a);
export const listBankAccounts   = (...a) => impl.listBankAccounts(...a);
export const listPayments       = (...a) => impl.listPayments(...a);

export { normaliseCode } from './codes.js';
